import { gradientPreset, patternTile } from "@keepoinc/site-page-react-component";
import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Background, Theme } from "../../../../../../model/theme";
import { classNames } from "../../../../../../utils/styles";
import { fieldLabel } from "../../styles";

/** The kinds of background, in the order they're offered. */
export const BACKGROUND_KINDS = ["color", "gradient", "blur", "pattern", "image", "video"] as const;
export type BackgroundKind = (typeof BACKGROUND_KINDS)[number];

export const KindsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  .label {
    ${fieldLabel}
  }
`;

/* Six cards in a row (three on a phone), whatever how many there are: each a sixth, never wider; a thumbnail of the
   kind, its name under it */
export const KindOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.5rem;

  @media (max-width: 640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  button {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    align-items: stretch;
    padding: 0.375rem 0.375rem 0.5rem;
    font-family: "Inter", sans-serif;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border: 2px solid transparent;
    border-radius: 0.75rem;

    &.selected {
      border-color: var(--color-brand-blue);
    }
  }

  /* the thumbnail clips what's drawn in it (the blur goes past its edges) */
  .frame {
    position: relative;
    overflow: hidden;
    aspect-ratio: 4 / 3;
    border-radius: 0.5rem;
  }

  .thumbnail {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8a8a96;
    background-position: center;
    background-size: cover;
  }
`;

const cover = (url: string): CSSProperties => ({ backgroundImage: `url("${url.replace(/"/g, "%22")}")` });

/** An image or a play sign, for the kinds with nothing uploaded yet. */
const Sign = ({ kind }: { kind: "image" | "video" }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    {kind === "image" ? (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <circle cx="9" cy="10" r="1.5" />
        <path d="M21 16l-5-5-8 8" />
      </>
    ) : (
      <path d="M8 5v14l11-7z" />
    )}
  </svg>
);

type Props = {
  value: BackgroundKind;
  onSelect: (kind: BackgroundKind) => void;
  /** The Keepo's colors and its background now: the thumbnails show them. */
  theme: Theme;
  /** The Welcome section's photo, which the blur's thumbnail shows. */
  profilePhoto?: string;
  /** What the other kinds had while editing (an image added, a gradient made): their cards keep showing it. */
  kept?: Partial<Record<BackgroundKind, Background>>;
  /** PLUS only: any choice asks to subscribe (`onLocked`) instead. */
  locked?: boolean;
  onLocked?: () => void;
};

/** "Estilo do fundo": Cor, Gradiente, Desfoque, Padrão, Imagem, Vídeo, each a card showing it in the Keepo's colors. */
export const BackgroundKinds = ({ value, onSelect, theme, profilePhoto, kept, locked, onLocked }: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.editor.settings.groups.backgroundKinds";
  const label = t("pages.editor.settings.groups.backgroundStyle");
  // each card shows its own: the background now when it's that kind, or what the kind kept
  const own = (kind: BackgroundKind): Background | undefined =>
    theme.background?.type === kind ? theme.background : kept?.[kind];
  const light = "linear-gradient(135deg, #e4e4ea, #f6f6f8)";

  const thumbnail = (kind: BackgroundKind): CSSProperties => {
    const background = own(kind);
    switch (kind) {
      case "color":
        return { backgroundColor: theme.primary };
      case "gradient":
        if (background?.type === "gradient" && background.preset) {
          return gradientPreset(background.preset, theme.primary, theme.buttons) ?? {};
        }
        return background?.type === "gradient"
          ? {
              backgroundImage: background.radial
                ? `radial-gradient(circle at 50% 40%, ${background.from}, ${background.to})`
                : `linear-gradient(${background.angle}deg, ${background.from}, ${background.to})`,
            }
          : { backgroundImage: `linear-gradient(180deg, ${theme.primary}, ${theme.buttons})` };
      case "blur":
        return {
          ...(profilePhoto ? cover(profilePhoto) : { backgroundImage: light }),
          filter: "blur(4px)",
          transform: "scale(1.2)",
        };
      case "pattern":
        return {
          backgroundColor: theme.primary,
          ...(background?.type === "pattern"
            ? patternTile(background.pattern, background.color, background.opacity, background.size)
            : patternTile("dot-grid", theme.secondary, 40)),
        };
      case "image":
        return background?.type === "image" && background.url ? cover(background.url) : { backgroundImage: light };
      case "video":
        return background?.type === "video" && background.poster
          ? cover(background.poster)
          : { backgroundImage: light };
    }
  };

  const hasOwn = (kind: BackgroundKind) => {
    const background = own(kind);
    return (background?.type === "image" && !!background.url) || (background?.type === "video" && !!background.poster);
  };

  return (
    <KindsContainer>
      <span className="label" aria-hidden="true">
        {label}
      </span>
      <KindOptions role="radiogroup" aria-label={label}>
        {BACKGROUND_KINDS.map(kind => (
          <button
            key={kind}
            type="button"
            role="radio"
            aria-checked={value === kind}
            className={classNames({ selected: value === kind })}
            onClick={() => (locked ? onLocked?.() : onSelect(kind))}>
            <span className="frame" aria-hidden="true">
              <span className="thumbnail" style={thumbnail(kind)}>
                {(kind === "image" || kind === "video") && !hasOwn(kind) && <Sign kind={kind} />}
              </span>
            </span>
            {t(`${prefix}.${kind}`)}
          </button>
        ))}
      </KindOptions>
    </KindsContainer>
  );
};
